// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';

export default function Login() {
  const { login }  = useAuth();
  const navigate   = useNavigate();
  const [form, setForm]       = useState({ email:'', password:'' });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [bizName, setBizName] = useState(localStorage.getItem('biz_name') || 'Snooker Club');
  const [bizTag,  setBizTag]  = useState(localStorage.getItem('biz_tagline') || 'Management System');
  const [logoUrl, setLogoUrl] = useState(localStorage.getItem('biz_logo') || '');

  useEffect(() => {
    // Load business settings (no auth needed)
    api.get('/settings').then(res => {
      if (res.data.business_name)    { setBizName(res.data.business_name); localStorage.setItem('biz_name', res.data.business_name); }
      if (res.data.business_tagline) { setBizTag(res.data.business_tagline); localStorage.setItem('biz_tagline', res.data.business_tagline); }
      if (res.data.logo_url !== undefined) { setLogoUrl(res.data.logo_url); localStorage.setItem('biz_logo', res.data.logo_url); }
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="login-page-snooker">
      {/* Animated balls background */}
      <div className="snooker-bg">
        {['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#8b5cf6','#ec4899','#1e3a5f'].map((color, i) => (
          <div key={i} className="snooker-ball" style={{
            background: color,
            width:  30 + (i*7) + 'px',
            height: 30 + (i*7) + 'px',
            left:   (i * 13) + '%',
            animationDelay: (i * 0.4) + 's',
            animationDuration: (6 + i) + 's',
          }}/>
        ))}
      </div>

      {/* Green baize table area */}
      <div className="login-table-strip"/>

      <div className="login-wrapper">
        {/* Left panel — branding */}
        <div className="login-brand-panel">
          <div className="login-brand-logo">
            {logoUrl
              ? <img src={logoUrl} alt="logo" style={{ width:90, height:90, borderRadius:16, objectFit:'cover' }}/>
              : <div className="login-ball-icon">🎱</div>
            }
          </div>
          <h1 className="login-brand-name">{bizName}</h1>
          <p className="login-brand-tag">{bizTag}</p>

          <div className="login-brand-features">
            <div className="login-feature-item">🎱 Table Management</div>
            <div className="login-feature-item">⏱ Smart Billing</div>
            <div className="login-feature-item">🍟 Food Orders</div>
            <div className="login-feature-item">📊 Reports & Analytics</div>
          </div>
        </div>

        {/* Right panel — login form */}
        <div className="login-form-panel">
          <div className="login-form-card">
            <div className="login-form-header">
              <h2>Welcome Back</h2>
              <p>Sign in to manage your club</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email or Username</label>
                <input
                  type="text" required autoFocus
                  value={form.email}
                  onChange={e => setForm(f => ({...f, email: e.target.value}))}
                  placeholder="admin@snookerclub.com"
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <div className="input-with-icon">
                  <input
                    type={showPwd ? 'text' : 'password'} required
                    value={form.password}
                    onChange={e => setForm(f => ({...f, password: e.target.value}))}
                    placeholder="••••••••"
                  />
                  <button type="button" className="input-eye" onClick={() => setShowPwd(v => !v)}>
                    {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn-login" disabled={loading}>
                <LogIn size={18}/>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
            <p className="login-hint">Default: admin / password</p>
          </div>
        </div>
      </div>
    </div>
  );
}
